import {
	ActionIcon,
	Button,
	Checkbox,
	Combobox,
	ComboboxItem,
	Input,
	InputBase,
	InputBaseProps,
	InputStylesNames,
	InputWrapperStylesNames,
	useCombobox,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import { ChevronDown, X } from 'lucide-react';
import { ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type MultiSelectItem = string | ComboboxItem;

export type MultiSelectParams = {
	data: MultiSelectItem[];
	value?: string[];
	onChange?: (value: string[]) => void;
	searchable?: boolean;
};

export const getMultiSelectItemValue = (item: MultiSelectItem) =>
	typeof item === 'string' ? item : item.value;

export const getMultiSelectItemLabel = (item: MultiSelectItem) =>
	typeof item === 'string' ? item : item.label;

export const useMultiSelect = ({
	data: rawData,
	value: controlledValue,
	onChange,
	searchable,
}: MultiSelectParams) => {
	const combobox = useCombobox({
		onDropdownOpen: () => {
			searchable && combobox.focusSearchInput();
		},
		onDropdownClose: () => {
			handleSearchChange('');
		},
	});

	const [value, setValue] = useUncontrolled({
		value: controlledValue,
		defaultValue: [],
		onChange,
	});

	const [search, setSearch] = useState('');

	const data = rawData.filter((item) =>
		getMultiSelectItemLabel(item).toLowerCase().includes(search.toLowerCase().trim()),
	);

	function handleValueSubmit(val: string) {
		setValue(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
	}

	function handleSelectAll() {
		setValue(rawData.map(getMultiSelectItemValue));
	}

	function handleClearAll() {
		setValue([]);
	}

	function handleSearchChange(s: string) {
		combobox.resetSelectedOption();
		setSearch(s);
	}

	return {
		combobox,
		data,
		value,
		search,
		handleValueSubmit,
		handleSelectAll,
		handleClearAll,
		handleSearchChange,
	};
};

export type MultiSelectProps = {
	data: MultiSelectItem[];
	value?: string[];
	placeholder?: string;
	searchPlaceholder?: string;
	nothingFoundMessage?: string;
	searchable?: boolean;
	clearable?: boolean;
	withDropdownHeader?: boolean;
	fullWidth?: boolean;
	classNames?: Partial<Record<InputStylesNames | InputWrapperStylesNames, string>>;
	onChange?: (value: string[]) => void;
} & Omit<InputBaseProps, 'classNames'> &
	Omit<ComponentPropsWithoutRef<'button'>, keyof InputBaseProps | 'onChange'>;

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
	(
		{
			data: rawData,
			value: controlledValue,
			placeholder,
			searchPlaceholder,
			nothingFoundMessage,
			searchable = true,
			withDropdownHeader,
			clearable = true,
			fullWidth,
			classNames,
			onChange,
			onClick,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation();

		const {
			combobox,
			data,
			value,
			search,
			handleValueSubmit,
			handleSelectAll,
			handleClearAll,
			handleSearchChange,
		} = useMultiSelect({
			data: rawData,
			value: controlledValue,
			onChange,
			searchable,
		});

		const inputValue = rawData
			.filter((item) => value.includes(getMultiSelectItemValue(item)))
			.map(getMultiSelectItemLabel)
			.join(', ');

		return (
			<Combobox
				store={combobox}
				offset={0}
				onOptionSubmit={handleValueSubmit}
			>
				<Combobox.Target>
					<InputBase
						ref={ref}
						data-test-id={`multi-select-input-${props.id}`}
						data-full-width={fullWidth || undefined}
						component='button'
						type='button'
						rightSection={
							clearable && value.length > 0 ? (
								<ActionIcon
									className='multi-select-clear-button'
									data-test-id='multi-select-clear-button'
									variant='ghost-secondary'
									size='sm'
									tabIndex={-1}
									onPointerDown={(event) => event.preventDefault()}
									onClick={handleClearAll}
								>
									<X />
								</ActionIcon>
							) : (
								<ChevronDown className='multi-select-chevron' />
							)
						}
						rightSectionPointerEvents='none'
						classNames={{
							...classNames,
							root: clsx('multi-select-input-wrapper-root', classNames?.root),
							wrapper: clsx('multi-select-input-wrapper', classNames?.wrapper),
							input: clsx('multi-select-input-input', classNames?.input),
							section: clsx('multi-select-input-section', classNames?.section),
						}}
						onClick={(event) => {
							combobox.toggleDropdown();
							onClick?.(event);
						}}
						{...props}
					>
						{value.length ? inputValue : <Input.Placeholder>{placeholder}</Input.Placeholder>}
					</InputBase>
				</Combobox.Target>
				<Combobox.Dropdown className='multi-select-dropdown'>
					{searchable && (
						<Combobox.Search
							value={search}
							placeholder={searchPlaceholder}
							onChange={(event) => handleSearchChange(event.target.value)}
						/>
					)}
					{withDropdownHeader && (
						<Combobox.Header className='multi-select-header'>
							<Button
								variant='text'
								onClick={handleSelectAll}
							>
								{t('multi-select.select-all')}
							</Button>
						</Combobox.Header>
					)}
					{data.length === 0 && nothingFoundMessage && (
						<Combobox.Empty className='multi-select-empty'>{nothingFoundMessage}</Combobox.Empty>
					)}
					{data.length > 0 && (
						<Combobox.Options>
							{data.map((item) => {
								const itemValue = getMultiSelectItemValue(item);
								const itemLabel = getMultiSelectItemLabel(item);
								const itemActive = value.includes(getMultiSelectItemValue(item));

								return (
									<Combobox.Option
										key={itemValue}
										value={itemValue}
										active={itemActive}
										data-test-id={`multi-select-checkbox-${itemValue}`}
									>
										<Checkbox
											readOnly
											checked={itemActive}
											tabIndex={-1}
										/>
										<span className='multi-select-option-label'>{itemLabel}</span>
									</Combobox.Option>
								);
							})}
						</Combobox.Options>
					)}
				</Combobox.Dropdown>
			</Combobox>
		);
	},
);
